// Revision Exam — the SINGLE self-contained revision interactive. One slide IS
// the whole paper: a faithful React port of the reference exam app
// (revise-reference/NSC2025-main). Continuous question flow, the source panel
// swapping inline, global + per-question timers, the skills coach, a unified
// Level-1–7 results screen, review-wrong, and the 70%-unlock Mark This Answer
// bonus. Every question writes one learning_interactions row.
//
// See REVISION_TEMPLATE_PLAN.md. Coaching content is shared with the (now
// secondary) source-examination type via sourceExamCoaching.ts.

import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { AnimatePresence } from 'framer-motion';
import type {
  RevisionExamAttachment,
  RevisionExamSlideData,
  RevisionExamQuestion,
  RevisionExamSource,
  MarkThisAnswerSampleAnswer,
  Theme,
} from '../../slideTypes';
import {
  attachmentDownloadUrl,
  formatBytes,
  visibleAttachments,
} from '../../../../../lib/revision-builder/attachments';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import {
  SOURCE_EXAM_COACHING,
  SKILL_LABELS,
  COGNITIVE_LEVEL_LABELS,
} from './sourceExamCoaching';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: RevisionExamSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: RevisionExamSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}

const html = (s: string) => ({ dangerouslySetInnerHTML: { __html: s } });
type Verdict = 'yes' | 'partial' | 'no';
const VERDICTS: Verdict[] = ['yes', 'partial', 'no'];
const VERDICT_LABEL: Record<Verdict, string> = { yes: 'Yes', partial: 'Partial', no: 'No' };

function correctSet(q: RevisionExamQuestion): Set<number> {
  return new Set(Array.isArray(q.correct) ? q.correct : [q.correct]);
}
function isMulti(q: RevisionExamQuestion): boolean {
  return typeof q.multiSelectMax === 'number' && q.multiSelectMax > 1;
}
function recommendedSec(q: RevisionExamQuestion): number {
  return q.recommendedTimeSec ?? Math.max(30, (q.marks || 1) * 45);
}
function fmtClock(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
function fmtShort(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return (m > 0 ? `${m}m ` : '') + `${s}s`;
}

interface AnswerState {
  selected: number[];
  submitted: boolean;
  correct: boolean;
}

const LEVELS = ['L1_recall', 'L2_interpret', 'L3_apply'] as const;
const LEVEL_FULL: Record<(typeof LEVELS)[number], string> = {
  L1_recall: 'L1 · Recall / extraction',
  L2_interpret: 'L2 · Interpretation',
  L3_apply: 'L3 · Analysis / synthesis',
};

/** Reference grade bands (app.js showResults). */
function gradeFor(pct: number): { level: string; comment: string; tone: 'ok' | 'warn' | 'bad' } {
  if (pct >= 80) return { level: 'Level 7', tone: 'ok', comment: 'Outstanding. Thorough understanding of source analysis, interpretation, evaluation and synthesis.' };
  if (pct >= 70) return { level: 'Level 6', tone: 'ok', comment: 'Meritorious. Strong source skills; refine evaluation and comparison for full marks.' };
  if (pct >= 60) return { level: 'Level 5', tone: 'ok', comment: 'Substantial. You handle extraction and interpretation well. Develop your evaluation and synthesis.' };
  if (pct >= 50) return { level: 'Level 4', tone: 'warn', comment: 'Adequate. Extraction is solid. Focus on moving from WHAT to WHY: interpretation and evaluation.' };
  if (pct >= 40) return { level: 'Level 3', tone: 'warn', comment: 'Moderate. You can find information but struggle with interpretation. Ask: what does this evidence MEAN?' };
  if (pct >= 30) return { level: 'Level 2', tone: 'bad', comment: 'Elementary. Start with careful source reading and extraction before higher-order questions.' };
  return { level: 'Level 1', tone: 'bad', comment: 'Not yet achieved. Focus on reading sources carefully to find specific information.' };
}

export function RevisionExamSlide({ data, theme, isEditing, onUpdate, onActivityResponse }: Props) {
  if (isEditing) return <RevisionExamEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return <RevisionExamStudent data={data} theme={theme} onActivityResponse={onActivityResponse} />;
}

type Phase = 'exam' | 'paragraph' | 'results' | 'mta';
type ParagraphBuilder = NonNullable<RevisionExamSlideData['paragraphBuilder']>;
interface StepState { selected: number; submitted: boolean; correct: boolean }

function RevisionExamStudent({
  data,
  theme,
  onActivityResponse,
}: {
  data: RevisionExamSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const timed = data.timed !== false;
  const questions = data.questions;
  const sourceByKey = useMemo(() => {
    const m: Record<string, RevisionExamSource> = {};
    for (const s of data.sources) m[s.key] = s;
    return m;
  }, [data.sources]);
  const unlockPct = data.unlockPct ?? 70;

  const [phase, setPhase] = useState<Phase>('exam');
  const [curQ, setCurQ] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [coachOpen, setCoachOpen] = useState(false);
  const [sourceCollapsed, setSourceCollapsed] = useState(false);
  const [qElapsed, setQElapsed] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [perQuestionTime, setPerQuestionTime] = useState<Record<string, number>>({});

  // Guided paragraph builder (optional) — its marks count toward the score.
  const paraSteps = useMemo(() => data.paragraphBuilder?.steps ?? [], [data.paragraphBuilder]);
  const [paraAnswers, setParaAnswers] = useState<Record<string, StepState>>({});
  const paraTotal = useMemo(() => paraSteps.reduce((s, x) => s + (x.marks || 0), 0), [paraSteps]);
  const paraEarned = useMemo(
    () => paraSteps.reduce((s, x) => s + (paraAnswers[x.id]?.correct ? x.marks || 0 : 0), 0),
    [paraSteps, paraAnswers],
  );

  // Stable per-question option shuffle (kills A/B position bias).
  const shuffles = useMemo<Record<string, number[]>>(() => {
    const out: Record<string, number[]> = {};
    for (const q of questions) {
      const order = q.options.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      out[q.id] = order;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = questions[curQ];

  // Global exam clock — ticks across the whole paper, stops at results.
  useEffect(() => {
    if (!timed || phase !== 'exam') return;
    const id = setInterval(() => setTotalElapsed((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [timed, phase]);

  // Per-question clock — resets when the active question changes.
  useEffect(() => {
    if (!timed || phase !== 'exam') return;
    setQElapsed(0);
    const id = setInterval(() => setQElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [curQ, timed, phase]);

  const totalMarks = useMemo(() => questions.reduce((s, x) => s + (x.marks || 0), 0), [questions]);
  const earnedMarks = useMemo(
    () => questions.reduce((s, x) => s + (answers[x.id]?.correct ? x.marks || 0 : 0), 0),
    [questions, answers],
  );

  const ans = q ? answers[q.id] : undefined;
  const submitted = !!ans?.submitted;

  const select = (displayIdx: number) => {
    if (!q || ans?.submitted) return;
    setAnswers((prev) => {
      const cur = prev[q.id]?.selected ?? [];
      let next: number[];
      if (isMulti(q)) {
        if (cur.includes(displayIdx)) next = cur.filter((i) => i !== displayIdx);
        else if (cur.length < (q.multiSelectMax ?? 1)) next = [...cur, displayIdx];
        else next = cur;
      } else next = [displayIdx];
      return { ...prev, [q.id]: { selected: next, submitted: false, correct: false } };
    });
  };

  const ready = (() => {
    if (!q || !ans || ans.submitted) return false;
    return isMulti(q) ? ans.selected.length === (q.multiSelectMax ?? 1) : ans.selected.length === 1;
  })();

  const submit = useCallback(() => {
    if (!q || !ans || !ready) return;
    const order = shuffles[q.id];
    const selectedOriginal = ans.selected.map((d) => order[d]).sort((a, b) => a - b);
    const cs = [...correctSet(q)].sort((a, b) => a - b);
    const correct = selectedOriginal.length === cs.length && selectedOriginal.every((v, i) => v === cs[i]);
    setAnswers((prev) => ({ ...prev, [q.id]: { selected: ans.selected, submitted: true, correct } }));
    setPerQuestionTime((prev) => ({ ...prev, [q.id]: (prev[q.id] || 0) + qElapsed }));
    onActivityResponse?.({
      activityType: 'revision-exam',
      mode: 'quiz',
      isCorrect: correct,
      score: correct ? 1 : 0,
      responseData: { questionId: q.id, selected: selectedOriginal, correct: cs, cognitiveLevel: q.cognitiveLevel },
    });
  }, [q, ans, ready, shuffles, qElapsed, onActivityResponse]);

  const goNext = () => {
    if (curQ < questions.length - 1) setCurQ((i) => i + 1);
    else if (paraSteps.length > 0) setPhase('paragraph');
    else setPhase('results');
  };
  const goPrev = () => { if (curQ > 0) setCurQ((i) => i - 1); };

  const reviewWrong = () => {
    const firstWrong = questions.findIndex((x) => answers[x.id]?.submitted && !answers[x.id]?.correct);
    if (firstWrong === -1) return;
    // Reopen wrong questions for another attempt; keep correct ones locked.
    setAnswers((prev) => {
      const next = { ...prev };
      for (const x of questions) {
        if (next[x.id]?.submitted && !next[x.id]?.correct) next[x.id] = { selected: [], submitted: false, correct: false };
      }
      return next;
    });
    setCurQ(firstWrong);
    setPhase('exam');
  };

  const containerStyle = { background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body } as const;

  if (!q && phase === 'exam') {
    return <div className="h-full w-full grid place-items-center" style={containerStyle}><p style={{ color: theme.colors.textMuted }}>This exam has no questions yet.</p></div>;
  }

  // ── Paragraph builder phase ───────────────────────────────────────────────
  if (phase === 'paragraph' && data.paragraphBuilder) {
    return (
      <ParagraphBuilderView
        builder={data.paragraphBuilder}
        answers={paraAnswers}
        setAnswers={setParaAnswers}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onDone={() => setPhase('results')}
        onBack={() => { setPhase('exam'); setCurQ(questions.length - 1); }}
      />
    );
  }

  // ── Results phase ─────────────────────────────────────────────────────────
  if (phase === 'results') {
    return (
      <ResultsView
        data={data}
        theme={theme}
        questions={questions}
        answers={answers}
        paraSteps={paraSteps}
        paraAnswers={paraAnswers}
        earned={earnedMarks + paraEarned}
        total={totalMarks + paraTotal}
        totalElapsed={timed ? totalElapsed : null}
        perQuestionTime={perQuestionTime}
        unlockPct={unlockPct}
        onReview={reviewWrong}
        onStartBonus={() => setPhase('mta')}
      />
    );
  }

  // ── Mark This Answer bonus phase ──────────────────────────────────────────
  if (phase === 'mta' && data.markThisAnswer) {
    return (
      <MtaView
        task={data.markThisAnswer.task}
        rubricNote={data.markThisAnswer.rubricNote}
        answers={data.markThisAnswer.answers}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onBack={() => setPhase('results')}
      />
    );
  }

  // ── Exam phase ────────────────────────────────────────────────────────────
  const order = shuffles[q.id];
  const cs = correctSet(q);
  const src = sourceByKey[q.sourceKey];
  const coaching = SOURCE_EXAM_COACHING[q.skill];
  const shownEarned = Math.min(earnedMarks, totalMarks);

  return (
    <div className="h-full w-full overflow-y-auto" style={containerStyle}>
      <div className="mx-auto max-w-[760px] px-6 py-8">
        {/* Top bar: source · progress · timers · score */}
        <div className="flex items-center gap-3 mb-5 text-xs flex-wrap" style={{ color: theme.colors.textMuted }}>
          <span className="font-semibold uppercase tracking-wider">{src?.label ?? 'Source'}</span>
          <div className="flex-1 min-w-[80px] h-1.5 rounded-full overflow-hidden" style={{ background: `${theme.colors.textMuted}22` }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${(curQ / questions.length) * 100}%`, background: accent }} />
          </div>
          {timed && (
            <span className="flex items-center gap-2">
              <Timer elapsed={qElapsed} recommended={recommendedSec(q)} theme={theme} />
              <span className="tabular-nums" title="Total time">{fmtClock(totalElapsed)}</span>
            </span>
          )}
          <span className="font-semibold whitespace-nowrap" style={{ color: theme.colors.text }}>
            {shownEarned}/{totalMarks} · Q{curQ + 1}/{questions.length}
          </span>
        </div>

        <AttachmentBar
          attachments={visibleAttachments(data.attachments, 'during')}
          theme={theme}
          accent={accent}
          label="The paper"
        />

        {/* Source panel */}
        {src && (
          <div className="rounded-lg mb-6 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
            <div className="flex items-center justify-between px-5 pt-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{src.label}</span>
                <span className="text-xs ml-2" style={{ color: theme.colors.textMuted }}>{src.type}</span>
              </div>
              <button onClick={() => setSourceCollapsed((c) => !c)} className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>
                {sourceCollapsed ? '▸ Show source' : '▾ Hide source'}
              </button>
            </div>
            {!sourceCollapsed && (
              <div className="px-5 pb-4 pt-2">
                {src.imageUrl && <SourceImage url={src.imageUrl} alt={src.imageAlt ?? ''} theme={theme} />}
                {src.body && <div className="text-sm leading-relaxed" style={{ color: theme.colors.text }} {...html(src.body)} />}
                <p className="text-xs italic mt-3" style={{ color: theme.colors.textMuted }} {...html(src.attribution)} />
              </div>
            )}
          </div>
        )}

        {/* Question meta */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs font-bold" style={{ color: theme.colors.textMuted }}>Q{q.number}</span>
          <span className="text-[0.65rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${accent}1a`, color: accent }}>{SKILL_LABELS[q.skill]}</span>
          <span className="text-[0.65rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${theme.colors.textMuted}1a`, color: theme.colors.textMuted }}>{COGNITIVE_LEVEL_LABELS[q.cognitiveLevel]}</span>
          <span className="text-xs ml-auto" style={{ color: theme.colors.textMuted }}>{q.marks} mark{q.marks > 1 ? 's' : ''}</span>
        </div>

        <p className="text-base font-semibold mb-2" style={{ color: theme.colors.text }} {...html(q.prompt)} />
        {q.hint && <p className="text-sm mb-3 px-3 py-2 rounded" style={{ background: `${accent}10`, color: theme.colors.textMuted }} {...html(q.hint)} />}

        {/* Coach */}
        <button onClick={() => setCoachOpen(true)} className="text-xs font-medium mb-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: `${accent}12`, color: accent, border: `1px solid ${accent}40` }}>
          <span aria-hidden>＊</span> {coaching.buttonText}
        </button>

        {/* Options */}
        <div className="space-y-2.5 mb-4">
          {order.map((originalIdx, displayIdx) => {
            const selected = (ans?.selected ?? []).includes(displayIdx);
            const isCorrectOption = cs.has(originalIdx);
            let mark: string | null = null;
            let borderColor = `${theme.colors.textMuted}33`;
            let bg = theme.colors.surface;
            if (submitted) {
              if (isCorrectOption) { mark = '✓'; borderColor = feedbackStyle(theme, 'correct').text; bg = `${feedbackStyle(theme, 'correct').text}12`; }
              else if (selected) { mark = '✗'; borderColor = feedbackStyle(theme, 'incorrect').text; bg = `${feedbackStyle(theme, 'incorrect').text}12`; }
            } else if (selected) { borderColor = accent; bg = `${accent}12`; }
            return (
              <button key={displayIdx} onClick={() => select(displayIdx)} disabled={submitted}
                className="w-full text-left rounded-lg px-4 py-3 border-2 flex items-start gap-3 transition-all text-sm"
                style={{ background: bg, borderColor, cursor: submitted ? 'default' : 'pointer', opacity: submitted && !isCorrectOption && !selected ? 0.55 : 1, color: theme.colors.text }}>
                <span className="font-bold" style={{ color: submitted ? borderColor : accent }}>{'ABCDE'[displayIdx]}.</span>
                <span className="flex-1" {...html(q.options[originalIdx])} />
                {mark && <span className="font-bold" style={{ color: borderColor }}>{mark}</span>}
              </button>
            );
          })}
        </div>

        {/* Submit / feedback */}
        {!submitted ? (
          <div className="text-right">
            <button onClick={submit} disabled={!ready} className="px-6 py-2.5 rounded-lg font-semibold text-sm" style={{ background: accent, color: '#fff', cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.4 }}>Submit</button>
          </div>
        ) : (
          <AnimatePresence>
            <Reveal variant="up">
              <div className="rounded-lg p-4 border-l-4" style={{ background: ans!.correct ? feedbackStyle(theme, 'correct').bg : feedbackStyle(theme, 'incorrect').bg, borderColor: ans!.correct ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>
                <p className="text-sm font-bold mb-1" style={{ color: ans!.correct ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>
                  {ans!.correct ? `✓ Correct — ${q.marks} mark${q.marks > 1 ? 's' : ''} earned` : '✗ Not quite. 0 marks'}
                </p>
                <div className="text-sm leading-relaxed" style={{ color: theme.colors.text }} {...html(q.feedback)} />
              </div>
            </Reveal>
          </AnimatePresence>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-6">
          <button onClick={goPrev} disabled={curQ === 0} className="px-5 py-2 rounded-lg text-sm font-medium" style={{ background: theme.colors.surface, color: theme.colors.text, border: `1px solid ${theme.colors.textMuted}33`, opacity: curQ === 0 ? 0.4 : 1, cursor: curQ === 0 ? 'not-allowed' : 'pointer' }}>← Previous</button>
          <button onClick={goNext} disabled={!submitted} className="px-5 py-2 rounded-lg text-sm font-semibold" style={{ background: accent, color: '#fff', opacity: submitted ? 1 : 0.4, cursor: submitted ? 'pointer' : 'not-allowed' }}>
            {curQ === questions.length - 1 ? 'View results →' : 'Next →'}
          </button>
        </div>
      </div>

      {coachOpen && <CoachModal coaching={coaching} accent={accent} theme={theme} onClose={() => setCoachOpen(false)} />}
    </div>
  );
}

// ── Paper downloads ──────────────────────────────────────────────────────────
// The original PDFs the module was built from. Published exam papers, hosted in
// the public course-assets bucket, so a plain anchor is the whole mechanism.
const ATTACHMENT_LABEL: Record<RevisionExamAttachment['kind'], string> = {
  questionPaper: 'Question paper',
  addendum: 'Addendum (sources)',
  memo: 'Marking memo',
};

function AttachmentBar({
  attachments,
  theme,
  accent,
  label,
  center = false,
}: {
  attachments: RevisionExamAttachment[];
  theme: Theme;
  accent: string;
  label: string;
  /** Centre the row — the results screen's header block is centred. */
  center?: boolean;
}) {
  if (attachments.length === 0) return null;
  return (
    <div className={`flex items-center gap-2 flex-wrap mb-5 ${center ? 'justify-center' : ''}`}>
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.colors.textMuted }}>
        {label}
      </span>
      {attachments.map((a) => {
        const size = formatBytes(a.sizeBytes);
        return (
          <a
            key={`${a.kind}-${a.url}`}
            href={attachmentDownloadUrl(a)}
            // `download` is ignored cross-origin — the ?download= param on the
            // URL is what actually sets Content-Disposition. Kept for the
            // same-origin case and for the affordance it gives the browser UI.
            download={a.fileName}
            rel="noopener"
            className="text-xs font-medium inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-opacity hover:opacity-80"
            style={{ borderColor: `${accent}40`, color: accent, background: `${accent}0f` }}
          >
            <span aria-hidden>⭳</span>
            {ATTACHMENT_LABEL[a.kind]}
            {size && <span style={{ color: theme.colors.textMuted }}>· {size}</span>}
          </a>
        );
      })}
    </div>
  );
}

// ── Source image (with low-resolution advisory, §11 #10) ─────────────────────
function SourceImage({ url, alt, theme }: { url: string; alt: string; theme: Theme }) {
  const [lowRes, setLowRes] = useState(false);
  return (
    <div className="mb-3">
      <img
        src={url}
        alt={alt}
        className="max-w-full rounded"
        onLoad={(e) => { const img = e.currentTarget; if (img.naturalWidth > 0 && img.naturalWidth < 600) setLowRes(true); }}
      />
      {lowRes && (
        <p className="text-xs mt-1 italic" style={{ color: feedbackStyle(theme, 'warning').text }}>
          ⚠ This source image is low resolution and may be hard to read.
        </p>
      )}
    </div>
  );
}

// ── Timer ───────────────────────────────────────────────────────────────────
function Timer({ elapsed, recommended, theme }: { elapsed: number; recommended: number; theme: Theme }) {
  const remaining = recommended - elapsed;
  const over = remaining < 0;
  const colour = over ? feedbackStyle(theme, 'incorrect').text : remaining > recommended * 0.3 ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'warning').text;
  const mag = Math.abs(remaining);
  return <span className="font-bold tabular-nums whitespace-nowrap" style={{ color: colour }} title="Recommended time for this question — a guide, not a penalty">{`${over ? '+' : ''}${Math.floor(mag / 60)}:${String(mag % 60).padStart(2, '0')}`}</span>;
}

// ── Skills-coach modal ───────────────────────────────────────────────────────
function CoachModal({ coaching, accent, theme, onClose }: { coaching: (typeof SOURCE_EXAM_COACHING)[keyof typeof SOURCE_EXAM_COACHING]; accent: string; theme: Theme; onClose: () => void; }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="max-w-[560px] w-full max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl" style={{ background: theme.colors.background, color: theme.colors.text }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: `${theme.colors.textMuted}22` }}>
          <span className="font-bold" style={{ color: accent }}>{coaching.title}</span>
          <button onClick={onClose} aria-label="Close" className="text-xl leading-none" style={{ color: theme.colors.textMuted }}>×</button>
        </div>
        <div className="px-6 py-5">
          <h3 className="text-sm font-semibold mb-2" style={{ color: theme.colors.text }}>{coaching.sub}</h3>
          <p className="text-sm mb-4 leading-relaxed" style={{ color: theme.colors.textMuted }} {...html(coaching.intro)} />
          <div className="space-y-3 mb-4">
            {coaching.steps.map((s, i) => (
              <div key={i} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 grid place-items-center rounded-full text-xs font-bold" style={{ background: `${accent}1a`, color: accent }}>{i + 1}</span>
                <p className="text-sm leading-relaxed" style={{ color: theme.colors.text }}><strong>{s[0]}</strong> {s[1]}</p>
              </div>
            ))}
          </div>
          <div className="text-sm rounded-lg p-3 mb-3" style={{ background: `${accent}10`, color: theme.colors.text }}><strong>Example.</strong> <span {...html(coaching.example)} /></div>
          <div className="text-sm rounded-lg p-3" style={{ background: `${feedbackStyle(theme, 'warning').text}12`, color: theme.colors.text }}><strong>Common mistakes.</strong> <span {...html(coaching.warn)} /></div>
        </div>
      </div>
    </div>
  );
}

// ── Results view ─────────────────────────────────────────────────────────────
function levelTotals(
  questions: RevisionExamQuestion[],
  answers: Record<string, AnswerState>,
  paraSteps: ParagraphBuilder['steps'],
  paraAnswers: Record<string, StepState>,
) {
  const out: Record<(typeof LEVELS)[number], { earned: number; total: number }> = {
    L1_recall: { earned: 0, total: 0 }, L2_interpret: { earned: 0, total: 0 }, L3_apply: { earned: 0, total: 0 },
  };
  for (const q of questions) {
    out[q.cognitiveLevel].total += q.marks || 0;
    if (answers[q.id]?.correct) out[q.cognitiveLevel].earned += q.marks || 0;
  }
  // The synthesis paragraph is L3.
  for (const s of paraSteps) {
    out.L3_apply.total += s.marks || 0;
    if (paraAnswers[s.id]?.correct) out.L3_apply.earned += s.marks || 0;
  }
  return out;
}

function ResultsView({
  data, theme, questions, answers, paraSteps, paraAnswers, earned, total, totalElapsed, perQuestionTime, unlockPct, onReview, onStartBonus,
}: {
  data: RevisionExamSlideData; theme: Theme; questions: RevisionExamQuestion[]; answers: Record<string, AnswerState>;
  paraSteps: ParagraphBuilder['steps']; paraAnswers: Record<string, StepState>;
  earned: number; total: number; totalElapsed: number | null; perQuestionTime: Record<string, number>;
  unlockPct: number; onReview: () => void; onStartBonus: () => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const capped = Math.min(earned, total);
  const pct = total ? Math.round((capped / total) * 100) : 0;
  const grade = gradeFor(pct);
  const gradeColour = grade.tone === 'ok' ? feedbackStyle(theme, 'correct').text : grade.tone === 'warn' ? feedbackStyle(theme, 'warning').text : feedbackStyle(theme, 'incorrect').text;
  const lt = levelTotals(questions, answers, paraSteps, paraAnswers);
  const wrongCount = questions.filter((q) => answers[q.id]?.submitted && !answers[q.id]?.correct).length;
  const slowest = Object.entries(perQuestionTime)
    .map(([id, t]) => { const q = questions.find((x) => x.id === id); return q ? { num: q.number, t } : null; })
    .filter(Boolean).sort((a, b) => b!.t - a!.t).slice(0, 3) as { num: string; t: number }[];
  const unlocked = pct >= unlockPct;

  return (
    <div className="h-full w-full overflow-y-auto" style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}>
      <div className="mx-auto max-w-[680px] px-6 py-10">
        <header className="text-center mb-8">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{data.title || 'Examination complete'}</span>
          <div className="mt-3 text-5xl font-bold" style={{ color: gradeColour }}>{capped}<span className="text-2xl" style={{ color: theme.colors.textMuted }}> / {total}</span></div>
          <p className="text-sm mt-1" style={{ color: theme.colors.textMuted }}>{pct}%{totalElapsed != null && <> · {fmtClock(totalElapsed)} total</>}</p>
          <div className="mt-3 text-xl font-bold" style={{ color: gradeColour }}>{grade.level}</div>
          <p className="text-sm mt-2 max-w-[460px] mx-auto" style={{ color: theme.colors.textMuted }}>{grade.comment}</p>
        </header>

        {/* Everything downloadable, including the memo — the attempt is over, so
            the answer key is now the useful thing rather than a spoiler. */}
        <AttachmentBar
          attachments={visibleAttachments(data.attachments, 'after-results')}
          theme={theme}
          accent={accent}
          label="Download"
          center
        />

        <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: theme.colors.textMuted }}>By cognitive level</h2>
        <div className="space-y-3 mb-8">
          {LEVELS.filter((l) => lt[l].total > 0).map((l) => {
            const lpct = lt[l].total ? Math.round((lt[l].earned / lt[l].total) * 100) : 0;
            const colour = lpct >= 70 ? feedbackStyle(theme, 'correct').text : lpct >= 40 ? feedbackStyle(theme, 'warning').text : feedbackStyle(theme, 'incorrect').text;
            return (
              <div key={l}>
                <div className="flex justify-between text-xs mb-1" style={{ color: theme.colors.text }}><span>{LEVEL_FULL[l]}</span><span style={{ color: theme.colors.textMuted }}>{lt[l].earned}/{lt[l].total} · {lpct}%</span></div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: `${theme.colors.textMuted}22` }}><div className="h-full rounded-full" style={{ width: `${lpct}%`, background: colour }} /></div>
              </div>
            );
          })}
        </div>

        {slowest.length > 0 && (
          <>
            <h2 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: theme.colors.textMuted }}>Slowest questions</h2>
            <div className="space-y-1 mb-8">
              {slowest.map((t) => (
                <div key={t.num} className="flex justify-between text-sm px-3 py-1.5 rounded" style={{ background: theme.colors.surface }}>
                  <span style={{ color: theme.colors.text }}>Q{t.num}</span><span style={{ color: theme.colors.textMuted }}>{fmtShort(t.t)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {data.markThisAnswer && (
          unlocked ? (
            <div className="rounded-xl p-5 mb-4 text-center" style={{ background: `${accent}12`, border: `1px solid ${accent}40` }}>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>★ Bonus unlocked</span>
              <p className="text-sm mt-1 mb-3" style={{ color: theme.colors.text }}>You scored {pct}%. Evaluate two answers like an examiner for extra marks.</p>
              <button onClick={onStartBonus} className="px-5 py-2 rounded-lg font-semibold text-sm text-white" style={{ background: accent }}>Start the bonus →</button>
            </div>
          ) : (
            <div className="rounded-xl p-4 mb-4 text-center" style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.textMuted}22` }}>
              <p className="text-sm" style={{ color: theme.colors.textMuted }}>🔒 Score {unlockPct}% to unlock the <strong style={{ color: theme.colors.text }}>Mark This Answer</strong> bonus. You're at {pct}%.</p>
            </div>
          )
        )}

        {wrongCount > 0 && (
          <div className="text-center">
            <button onClick={onReview} className="px-5 py-2 rounded-lg text-sm font-semibold" style={{ background: theme.colors.surface, color: theme.colors.text, border: `1px solid ${theme.colors.textMuted}33` }}>Review wrong answers ({wrongCount})</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mark This Answer view ────────────────────────────────────────────────────
function MtaView({
  task, rubricNote, answers, theme, onActivityResponse, onBack,
}: {
  task: string; rubricNote?: string; answers: MarkThisAnswerSampleAnswer[]; theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void; onBack: () => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const [selections, setSelections] = useState<Record<string, Record<string, Verdict>>>({});
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const pick = (aid: string, cid: string, v: Verdict) => { if (checked[aid]) return; setSelections((p) => ({ ...p, [aid]: { ...p[aid], [cid]: v } })); };
  const allRated = (a: MarkThisAnswerSampleAnswer) => a.criteria.every((c) => selections[a.id]?.[c.id] != null);
  const check = (a: MarkThisAnswerSampleAnswer) => {
    if (!allRated(a) || checked[a.id]) return;
    const correctCount = a.criteria.filter((c) => selections[a.id]?.[c.id] === c.correct).length;
    setChecked((p) => ({ ...p, [a.id]: true }));
    onActivityResponse?.({ activityType: 'revision-exam', mode: 'quiz', score: a.criteria.length ? correctCount / a.criteria.length : 0, responseData: { answerId: a.id, judgements: selections[a.id] ?? {}, correctCount, total: a.criteria.length } });
  };
  const bonus = answers.reduce((n, a) => n + (checked[a.id] ? a.criteria.filter((c) => selections[a.id]?.[c.id] === c.correct).length : 0), 0);
  const totalCriteria = answers.reduce((n, a) => n + a.criteria.length, 0);

  return (
    <div className="h-full w-full overflow-y-auto" style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}>
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="mb-5 flex items-center justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>★ Bonus · Mark this answer</span>
            <p className="text-xs mt-1" style={{ color: theme.colors.textMuted }}>{bonus} bonus mark{bonus === 1 ? '' : 's'} so far</p>
          </div>
          <button onClick={onBack} className="text-sm px-4 py-2 rounded-lg" style={{ background: theme.colors.surface, color: theme.colors.text, border: `1px solid ${theme.colors.textMuted}33` }}>← Results</button>
        </header>
        <div className="rounded-lg p-4 mb-5 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>The question</p>
          <div className="text-sm" style={{ color: theme.colors.text }} {...html(task)} />
          {rubricNote && <p className="text-xs italic mt-2" style={{ color: theme.colors.textMuted }} {...html(rubricNote)} />}
        </div>

        {answers.map((a) => {
          const isChecked = !!checked[a.id];
          const correctCount = a.criteria.filter((c) => selections[a.id]?.[c.id] === c.correct).length;
          return (
            <div key={a.id} className="mb-8">
              <div className="flex items-center gap-2 mb-2"><span className="text-sm font-bold" style={{ color: theme.colors.text }}>{a.label}</span><span className="text-xs font-semibold" style={{ color: accent }}>Evaluate this answer</span></div>
              <div className="rounded-lg p-4 mb-3 text-sm leading-relaxed" style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.textMuted}22`, color: theme.colors.text }} {...html(a.text)} />
              {a.criteria.map((c) => {
                const sel = selections[a.id]?.[c.id];
                const isRight = sel === c.correct;
                return (
                  <div key={c.id} className="mb-3">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm flex-1" style={{ color: theme.colors.text }}>{c.prompt}</span>
                      <div className="flex gap-1.5 shrink-0">
                        {VERDICTS.map((v) => {
                          const active = sel === v;
                          return <button key={v} onClick={() => pick(a.id, c.id, v)} disabled={isChecked} className="px-2.5 py-1 rounded-md text-xs font-semibold border" style={{ background: active ? `${accent}1a` : 'transparent', borderColor: active ? accent : `${theme.colors.textMuted}33`, color: active ? accent : theme.colors.textMuted, cursor: isChecked ? 'default' : 'pointer' }}>{VERDICT_LABEL[v]}</button>;
                        })}
                      </div>
                    </div>
                    {isChecked && (
                      <div className="mt-2 rounded-md p-2.5 text-xs leading-relaxed" style={{ background: isRight ? feedbackStyle(theme, 'correct').bg : feedbackStyle(theme, 'incorrect').bg, color: theme.colors.text }}>
                        <span className="font-bold" style={{ color: isRight ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>{isRight ? '✓ Correct' : `✗ Examiner: ${VERDICT_LABEL[c.correct]}`}</span> <span {...html(c.explanation)} />
                      </div>
                    )}
                  </div>
                );
              })}
              {!isChecked ? (
                <div className="text-right"><button onClick={() => check(a)} disabled={!allRated(a)} className="px-5 py-2 rounded-lg font-semibold text-sm text-white" style={{ background: accent, opacity: allRated(a) ? 1 : 0.4, cursor: allRated(a) ? 'pointer' : 'not-allowed' }}>Check my evaluation</button></div>
              ) : (
                <div className="rounded-lg p-3 text-sm" style={{ background: `${accent}12`, border: `1px solid ${accent}40` }}>
                  <span className="font-bold" style={{ color: accent }}>{correctCount}/{a.criteria.length} correct</span>
                  {a.approxBand && <span style={{ color: theme.colors.textMuted }}> — this answer would score about <strong style={{ color: theme.colors.text }}>{a.approxBand}</strong>.</span>}
                </div>
              )}
            </div>
          );
        })}
        <p className="text-center text-sm" style={{ color: theme.colors.textMuted }}>Total bonus: <strong style={{ color: accent }}>{bonus}</strong> / {totalCriteria}</p>
      </div>
    </div>
  );
}

// ── Paragraph builder view (guided 8-mark synthesis, §11 #7) ─────────────────
// Pick-from-choices only — never AI-graded free text. Four steps build the
// paragraph (topic → evidence → knowledge → conclusion); chosen sentences stack
// into a live "your paragraph so far" preview. Marks count toward the score.
function ParagraphBuilderView({
  builder, answers, setAnswers, theme, onActivityResponse, onDone, onBack,
}: {
  builder: ParagraphBuilder;
  answers: Record<string, StepState>;
  setAnswers: Dispatch<SetStateAction<Record<string, StepState>>>;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onDone: () => void;
  onBack: () => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const steps = builder.steps;
  const totalMarks = steps.reduce((s, x) => s + (x.marks || 0), 0);
  const [cur, setCur] = useState(0);
  const step = steps[cur];
  const st = step ? answers[step.id] : undefined;
  const submitted = !!st?.submitted;
  const allDone = steps.every((s) => answers[s.id]?.submitted);

  const select = (idx: number) => {
    if (!step || st?.submitted) return;
    setAnswers((p) => ({ ...p, [step.id]: { selected: idx, submitted: false, correct: false } }));
  };
  const submit = () => {
    if (!step || st?.submitted || st?.selected == null) return;
    const correct = st.selected === step.correct;
    setAnswers((p) => ({ ...p, [step.id]: { selected: st.selected, submitted: true, correct } }));
    onActivityResponse?.({
      activityType: 'revision-exam',
      mode: 'quiz',
      isCorrect: correct,
      score: correct ? 1 : 0,
      responseData: { paragraphStep: step.id, selected: st.selected, correct: step.correct, cognitiveLevel: 'L3_apply' },
    });
  };

  if (!step) return null;
  const ready = st?.selected != null && !st.submitted;
  // The paragraph assembled so far (the correct sentence for each submitted step).
  const builtSoFar = steps
    .filter((s) => answers[s.id]?.submitted)
    .map((s) => s.options[s.correct]);

  return (
    <div className="h-full w-full overflow-y-auto" style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}>
      <div className="mx-auto max-w-[760px] px-6 py-8">
        <header className="mb-4 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>✎ Build the paragraph · {totalMarks} marks</span>
          <button onClick={onBack} className="text-sm px-4 py-2 rounded-lg" style={{ background: theme.colors.surface, color: theme.colors.text, border: `1px solid ${theme.colors.textMuted}33` }}>← Questions</button>
        </header>

        <div className="rounded-lg p-4 mb-5 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>The paragraph question</p>
          <div className="text-sm" style={{ color: theme.colors.text }} {...html(builder.task)} />
          {builder.intro && <p className="text-xs italic mt-2" style={{ color: theme.colors.textMuted }} {...html(builder.intro)} />}
        </div>

        {/* Step chips */}
        <div className="flex items-center gap-2 mb-4 flex-wrap text-xs">
          {steps.map((s, i) => {
            const done = answers[s.id]?.submitted;
            const isCur = i === cur;
            return (
              <button key={s.id} onClick={() => setCur(i)} className="px-2.5 py-1 rounded-full font-semibold border" style={{ background: isCur ? `${accent}1a` : 'transparent', borderColor: isCur ? accent : `${theme.colors.textMuted}33`, color: isCur ? accent : theme.colors.textMuted }}>
                {done ? '✓ ' : `${i + 1}. `}{s.label}
              </button>
            );
          })}
        </div>

        {/* Built-so-far preview */}
        {builtSoFar.length > 0 && (
          <div className="rounded-lg p-3 mb-5 text-sm leading-relaxed" style={{ background: `${accent}10`, color: theme.colors.text }}>
            <span className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: accent }}>Your paragraph so far</span>
            {builtSoFar.join(' ')}
          </div>
        )}

        {/* Current step */}
        <p className="text-base font-semibold mb-3" style={{ color: theme.colors.text }}><span style={{ color: accent }}>{step.label}.</span> <span {...html(step.prompt)} /></p>
        <div className="space-y-2.5 mb-4">
          {step.options.map((opt, idx) => {
            const selected = st?.selected === idx;
            const isCorrect = idx === step.correct;
            let borderColor = `${theme.colors.textMuted}33`;
            let bg = theme.colors.surface;
            let mark: string | null = null;
            if (submitted) {
              if (isCorrect) { mark = '✓'; borderColor = feedbackStyle(theme, 'correct').text; bg = `${feedbackStyle(theme, 'correct').text}12`; }
              else if (selected) { mark = '✗'; borderColor = feedbackStyle(theme, 'incorrect').text; bg = `${feedbackStyle(theme, 'incorrect').text}12`; }
            } else if (selected) { borderColor = accent; bg = `${accent}12`; }
            return (
              <button key={idx} onClick={() => select(idx)} disabled={submitted} className="w-full text-left rounded-lg px-4 py-3 border-2 flex items-start gap-3 text-sm" style={{ background: bg, borderColor, cursor: submitted ? 'default' : 'pointer', opacity: submitted && !isCorrect && !selected ? 0.55 : 1, color: theme.colors.text }}>
                <span className="font-bold" style={{ color: submitted ? borderColor : accent }}>{'ABCDE'[idx]}.</span>
                <span className="flex-1" {...html(opt)} />
                {mark && <span className="font-bold" style={{ color: borderColor }}>{mark}</span>}
              </button>
            );
          })}
        </div>

        {!submitted ? (
          <div className="text-right">
            <button onClick={submit} disabled={!ready} className="px-6 py-2.5 rounded-lg font-semibold text-sm text-white" style={{ background: accent, cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.4 }}>Submit</button>
          </div>
        ) : (
          <>
            <AnimatePresence>
              <Reveal variant="up">
                <div className="rounded-lg p-4 border-l-4" style={{ background: st!.correct ? feedbackStyle(theme, 'correct').bg : feedbackStyle(theme, 'incorrect').bg, borderColor: st!.correct ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>
                  <p className="text-sm font-bold mb-1" style={{ color: st!.correct ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>{st!.correct ? '✓ Strong choice' : '✗ Not the strongest choice'}</p>
                  <div className="text-sm leading-relaxed" style={{ color: theme.colors.text }} {...html(step.feedback)} />
                </div>
              </Reveal>
            </AnimatePresence>
            <div className="flex justify-end mt-6">
              {cur < steps.length - 1 ? (
                <button onClick={() => setCur((i) => i + 1)} className="px-5 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: accent }}>Next step →</button>
              ) : (
                <button onClick={onDone} disabled={!allDone} className="px-5 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: accent, opacity: allDone ? 1 : 0.4, cursor: allDone ? 'pointer' : 'not-allowed' }}>View results →</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Editor (minimal — aiOnly) ────────────────────────────────────────────────
function RevisionExamEditor({ data, theme, onUpdate }: { data: RevisionExamSlideData; theme: Theme; onUpdate: (data: RevisionExamSlideData) => void; }) {
  const accent = theme.colors.accent || theme.colors.primary;
  const inputStyle = { background: theme.colors.background, borderColor: `${theme.colors.textMuted}33`, color: theme.colors.text } as const;
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px] space-y-4">
        <input value={data.title} onChange={(e) => onUpdate({ ...data, title: e.target.value })} placeholder="Title…" className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none" style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <label className="flex items-center gap-2 text-sm" style={{ color: theme.colors.text }}>
          <input type="checkbox" checked={data.timed !== false} onChange={(e) => onUpdate({ ...data, timed: e.target.checked })} /> Timed mode
        </label>
        <div className="rounded-lg border p-4 text-sm" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33`, color: theme.colors.textMuted }}>
          <p className="font-semibold mb-1" style={{ color: accent }}>Revision exam</p>
          <p>{data.sources.length} source{data.sources.length === 1 ? '' : 's'} · {data.questions.length} question{data.questions.length === 1 ? '' : 's'}{data.paragraphBuilder ? ' · + paragraph builder' : ''}{data.markThisAnswer ? ' · + Mark This Answer bonus' : ''}.</p>
          <p className="mt-2">This is a generated, self-contained exam. Use the Revision panel to regenerate, or edit the JSON via the AI build flow.</p>
        </div>

        {/* Downloadable originals — when students get each uploaded paper. */}
        {!!data.attachments?.length && (
          <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: theme.colors.textMuted }}>
              Downloads
            </p>
            <div className="space-y-2.5">
              {data.attachments.map((a, i) => (
                <div key={`${a.kind}-${a.url}`} className="flex items-center gap-2 flex-wrap">
                  <a
                    href={attachmentDownloadUrl(a)}
                    download={a.fileName}
                    rel="noopener"
                    className="text-sm font-medium underline underline-offset-2 truncate max-w-[220px]"
                    style={{ color: accent }}
                    title={a.fileName}
                  >
                    {ATTACHMENT_LABEL[a.kind]}
                  </a>
                  {formatBytes(a.sizeBytes) && (
                    <span className="text-xs" style={{ color: theme.colors.textMuted }}>{formatBytes(a.sizeBytes)}</span>
                  )}
                  <div className="flex items-center gap-1.5 ml-auto">
                    {(['always', 'after-results', 'hidden'] as const).map((v) => {
                      const active = a.visibility === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            const next = [...(data.attachments ?? [])];
                            next[i] = { ...a, visibility: v };
                            onUpdate({ ...data, attachments: next });
                          }}
                          className="text-xs font-medium px-2 py-0.5 rounded-full border"
                          style={{
                            borderColor: active ? accent : `${theme.colors.textMuted}33`,
                            color: active ? accent : theme.colors.textMuted,
                            background: active ? `${accent}1a` : 'transparent',
                          }}
                        >
                          {v === 'always' ? 'With exam' : v === 'after-results' ? 'After results' : 'Hidden'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: theme.colors.textMuted }}>
              These files are publicly hosted — anyone with the link can open them. Fine for published past papers; don't put anything confidential here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
